/**
 * Tenant Management Types
 *
 * Type definitions for tenant CRUD operations and UI state.
 */

import type { TenantId, Environment } from "@tatvex/types";

// ============================================================================
// Tenant Entity Types
// ============================================================================

/** Tenant status */
export type TenantStatus = "active" | "suspended" | "archived";

/** Tenant features that can be enabled/disabled */
export interface TenantFeatures {
  readonly journey: boolean;
  readonly comparison: boolean;
  readonly studio: boolean;
  readonly apiAccess: boolean;
  readonly whiteLabel: boolean;
}

/** Tenant quota limits */
export interface TenantQuotas {
  readonly maxVerticals: number;
  readonly maxProductsPerVertical: number;
  readonly maxUsersPerTenant: number;
  readonly maxApiRequestsPerDay: number;
  readonly maxStorageMb: number;
}

/** Tenant settings */
export interface TenantSettings {
  readonly features: TenantFeatures;
  readonly quotas: TenantQuotas;
  readonly allowedEnvironments: readonly Environment[];
}

/** Default features for new tenants */
export const DEFAULT_TENANT_FEATURES: TenantFeatures = {
  journey: true,
  comparison: true,
  studio: true,
  apiAccess: true,
  whiteLabel: false,
};

/** Default quotas for new tenants */
export const DEFAULT_TENANT_QUOTAS: TenantQuotas = {
  maxVerticals: 5,
  maxProductsPerVertical: 1000,
  maxUsersPerTenant: 10,
  maxApiRequestsPerDay: 10000,
  maxStorageMb: 500,
};

/** Tenant entity from API */
export interface Tenant {
  readonly id: TenantId;
  readonly externalId: string;
  readonly name: string;
  readonly slug: string;
  readonly status: TenantStatus;
  readonly settings: TenantSettings;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly usersCount?: number;
  readonly verticalsCount?: number;
}

/** Tenant list response */
export interface TenantListResponse {
  readonly items: readonly Tenant[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly hasMore: boolean;
}

/** Tenant create input */
export interface TenantCreateInput {
  readonly name: string;
  readonly slug: string;
  readonly settings?: Partial<TenantSettings>;
}

/** Tenant update input */
export interface TenantUpdateInput {
  readonly name?: string;
  readonly status?: TenantStatus;
  readonly settings?: Partial<TenantSettings>;
}

// ============================================================================
// UI State Types
// ============================================================================

/** Filter options for tenant list */
export interface TenantListFilters {
  readonly search?: string;
  readonly status?: TenantStatus | "all";
  readonly page?: number;
  readonly pageSize?: number;
}

/** Tab options for tenant detail page */
export type TenantDetailTab = "details" | "settings" | "activity";

/** Confirmation dialog types */
export type ConfirmationAction = "suspend" | "activate" | "archive" | "delete";

/** Feature labels for display */
export const FEATURE_LABELS: Record<keyof TenantFeatures, string> = {
  journey: "Journey Builder",
  comparison: "Comparison Engine",
  studio: "Studio Access",
  apiAccess: "API Access",
  whiteLabel: "White-Label Branding",
};

/** Feature descriptions for tooltips */
export const FEATURE_DESCRIPTIONS: Record<keyof TenantFeatures, string> = {
  journey: "Enable multi-step user journeys with form inputs",
  comparison: "Enable product comparison and ranking engine",
  studio: "Allow access to the configuration studio UI",
  apiAccess: "Allow programmatic API access",
  whiteLabel: "Enable custom branding and white-label features",
};

/** Tenant status labels and colors */
export const TENANT_STATUS_INFO: Record<TenantStatus, { label: string; color: string }> = {
  active: { label: "Active", color: "success" },
  suspended: { label: "Suspended", color: "warning" },
  archived: { label: "Archived", color: "muted" },
};
